import { supabase } from '../lib/supabase';
import { EXTRA_DIRECTIVE_EMBED } from '../utils/extraDirectiveNumber';

function toCamelCase(obj) {
  if (!obj) return obj;
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  }
  return camelObj;
}

function toSnakeCase(obj) {
  if (!obj) return obj;
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    snakeObj[snakeKey] = obj?.[key];
  }
  return snakeObj;
}

const subcontractChangeOrderService = {
  async getByProjectId(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('subcontract_change_orders')
        ?.select(`
          *,
          subcontract:subcontracts(
            id,
            contract_value,
            subcontractors(id, company_name, trade, contact_person),
            project_costs(id, cost_code, description)
          ),
          source_extra:project_extras(id, title, type, status, co_number, division, category, directive_id, ${EXTRA_DIRECTIVE_EMBED})
        `)
        ?.eq('project_id', projectId)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching change orders:', error?.message);
        return [];
      }

      return (data || [])?.map(row => ({
        ...toCamelCase(row),
        subcontract: row?.subcontract ? {
          ...toCamelCase(row?.subcontract),
          subcontractor: row?.subcontract?.subcontractors ? toCamelCase(row?.subcontract?.subcontractors) : null,
          projectCost: row?.subcontract?.project_costs ? toCamelCase(row?.subcontract?.project_costs) : null,
        } : null,
        sourceExtra: row?.source_extra ? toCamelCase(row?.source_extra) : null,
      }));
    } catch (err) {
      console.error('Error fetching change orders:', err);
      return [];
    }
  },

  async create(data) {
    try {
      const snakeData = toSnakeCase(data);
      const { data: result, error } = await supabase
        ?.from('subcontract_change_orders')
        ?.insert({
          project_id: snakeData?.project_id,
          subcontract_id: snakeData?.subcontract_id,
          source_extra_id: snakeData?.source_extra_id || null,
          co_number: snakeData?.co_number || null,
          description: snakeData?.description || null,
          notes: snakeData?.notes || null,
          amount: snakeData?.amount || 0,
          status: 'draft',
        })
        ?.select(`
          *,
          subcontract:subcontracts(
            id,
            contract_value,
            subcontractors(id, company_name, trade, contact_person),
            project_costs(id, cost_code, description)
          ),
          source_extra:project_extras(id, title, type, status, co_number, directive_id, ${EXTRA_DIRECTIVE_EMBED})
        `)
        ?.single();

      if (error) {
        console.error('Error creating change order:', error?.message);
        throw error;
      }

      return {
        ...toCamelCase(result),
        subcontract: result?.subcontract ? {
          ...toCamelCase(result?.subcontract),
          subcontractor: result?.subcontract?.subcontractors ? toCamelCase(result?.subcontract?.subcontractors) : null,
          projectCost: result?.subcontract?.project_costs ? toCamelCase(result?.subcontract?.project_costs) : null,
        } : null,
        sourceExtra: result?.source_extra ? toCamelCase(result?.source_extra) : null,
      };
    } catch (err) {
      console.error('Error creating change order:', err);
      throw err;
    }
  },

  async update(id, updateData) {
    try {
      const snakeData = toSnakeCase(updateData);
      const { data: result, error } = await supabase
        ?.from('subcontract_change_orders')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select(`
          *,
          subcontract:subcontracts(
            id,
            contract_value,
            subcontractors(id, company_name, trade, contact_person),
            project_costs(id, cost_code, description)
          ),
          source_extra:project_extras(id, title, type, status, co_number, directive_id, ${EXTRA_DIRECTIVE_EMBED})
        `)
        ?.single();

      if (error) {
        console.error('Error updating change order:', error?.message);
        throw error;
      }

      return {
        ...toCamelCase(result),
        subcontract: result?.subcontract ? {
          ...toCamelCase(result?.subcontract),
          subcontractor: result?.subcontract?.subcontractors ? toCamelCase(result?.subcontract?.subcontractors) : null,
          projectCost: result?.subcontract?.project_costs ? toCamelCase(result?.subcontract?.project_costs) : null,
        } : null,
        sourceExtra: result?.source_extra ? toCamelCase(result?.source_extra) : null,
      };
    } catch (err) {
      console.error('Error updating change order:', err);
      throw err;
    }
  },

  async updateStatus(id, status) {
    try {
      const updatePayload = { status };
      if (status === 'sent') {
        updatePayload.sent_at = new Date()?.toISOString();
      } else if (status === 'signed') {
        updatePayload.approved_at = new Date()?.toISOString();
      }

      return await this.update(id, updatePayload);
    } catch (err) {
      console.error('Error updating change order status:', err);
      throw err;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('subcontract_change_orders')
        ?.delete()
        ?.eq('id', id);

      if (error) {
        console.error('Error deleting change order:', error?.message);
        throw error;
      }

      return true;
    } catch (err) {
      console.error('Error deleting change order:', err);
      throw err;
    }
  },

  async getApprovedSumBySubcontractId(subcontractId) {
    try {
      const { data, error } = await supabase
        ?.from('subcontract_change_orders')
        ?.select('amount')
        ?.eq('subcontract_id', subcontractId)
        ?.eq('status', 'approved');

      if (error) {
        console.error('Error fetching approved change orders sum:', error?.message);
        return 0;
      }

      return (data || [])?.reduce((sum, row) => sum + (parseFloat(row?.amount) || 0), 0);
    } catch (err) {
      console.error('Error fetching approved change orders sum:', err);
      return 0;
    }
  },
};

export default subcontractChangeOrderService;
